import {ReactNode, useState} from 'react';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import './landing-page.css';
import {
  ArrowRight,
  c,
  CheckMark,
  Container,
  Reveal,
  SiteFooter,
  SiteHeader,
  useScrollToTopOnMount,
} from './mrigl-shell';
import {TemplatesShowcase} from './templates-showcase';

export function LandingPage() {
  useScrollToTopOnMount();
  return (
    <div
      className="mrigl-landing min-h-screen"
      style={{background: c.bg, color: c.ink}}
    >
      <DefaultMetaTags />
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-16 focus:top-16 focus:z-50 focus:rounded focus:bg-black focus:px-16 focus:py-8 focus:text-white"
      >
        Aller au contenu
      </a>
      <SiteHeader />
      <main id="main" className="mrigl-main">
        <Hero />
        <ProofStrip />
        <GlassFeatures />
        <ExamplePreview />
        <HowItWorks />
        <TemplatesShowcase />
        <GradientCta />
        <Included />
        <LaunchOffer />
        <Audience />
        <Trust />
        <Faq />
        <FinalCta />
      </main>
      <SiteFooter />
      <StickyMobileCta />
    </div>
  );
}

function StickyMobileCta() {
  return (
    <div
      className="sticky-mobile-cta fixed bottom-0 left-0 right-0 z-40 border-t md:hidden"
      style={{
        borderColor: c.rule,
        background: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        paddingTop: 12,
        paddingLeft: 16,
        paddingRight: 16,
        boxShadow: '0 -4px 16px rgba(10, 63, 197, 0.08)',
      }}
    >
      <div className="flex items-center gap-12">
        <div className="min-w-0 flex-1">
          <p
            className="m-0 truncate text-sm"
            style={{fontWeight: 700, color: c.ink, lineHeight: 1.2}}
          >
            Gratuit pendant le lancement
          </p>
          <p
            className="m-0 truncate text-xs"
            style={{color: c.inkMuted, lineHeight: 1.3}}
          >
            Aucun paiement demandé.
          </p>
        </div>
        <Link
          to="/register"
          className="btn-lime inline-flex shrink-0 items-center justify-center gap-6 rounded px-18 py-12 text-sm font-semibold"
        >
          Créer gratuitement
          <ArrowRight />
        </Link>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      className="preview-hero pb-72 md:pt-88 relative overflow-hidden pt-56 md:pb-112"
      aria-labelledby="hero-title"
    >
      <Container>
        <motion.div
          initial={{opacity: 0, y: 12}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.55, ease: [0.22, 0.61, 0.36, 1]}}
          className="max-w-900 mx-auto text-center"
        >
          <span className="preview-pill mb-24 inline-block">
            Offre de lancement. <strong>Gratuit maintenant.</strong>
          </span>
          <h1
            id="hero-title"
            className="display preview-hero-title mb-24 text-5xl md:text-7xl"
          >
            Créez un site clair pour votre projet{' '}
            <span className="gradient-text">sans complexité.</span>
          </h1>

          <p className="hero-subtitle mx-auto mb-30 max-w-720 text-lg leading-7">
            Mrigl.site aide les freelances, créateurs, artisans, cafés, cabinets
            et petits projets à publier une présence en ligne professionnelle
            gratuitement pendant notre lancement.
          </p>

          <div className="mb-16 flex items-center justify-center">
            <Link
              to="/register"
              className="mrigl-gradient-button"
              aria-label="Créer mon site gratuitement"
            >
              Créer mon site gratuitement
              <ArrowRight />
            </Link>
          </div>

          <p className="hero-note">
            Aucune carte bancaire • SSL inclus • Support 24/7
          </p>

          <div className="trust-strip">
            <div className="avatars" aria-hidden="true">
              <div className="avatar">F</div>
              <div className="avatar">C</div>
              <div className="avatar">A</div>
              <div className="avatar">+</div>
            </div>
            <p>
              <span className="star-row" aria-hidden="true">
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
                <StarIcon />
              </span>
              <br />
              Fait pour les freelances, cafés, artisans, consultants et petites
              structures en Tunisie.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{opacity: 0, y: 28, scale: 0.985}}
          animate={{opacity: 1, y: 0, scale: 1}}
          transition={{duration: 0.7, delay: 0.14, ease: [0.22, 0.61, 0.36, 1]}}
          className="hero-preview-stage mockup-section max-w-1080 mx-auto mt-44 md:mt-64"
        >
          <div className="hero-mockup-drift">
            <EditorScreenshot
              loading="eager"
              className="hero-editor-screenshot"
            />
          </div>
        </motion.div>
      </Container>
    </section>
  );
}

const proofItems = [
  ['0 TND', 'Gratuit pendant la phase de lancement.'],
  ['SSL inclus', "Votre site s'ouvre en connexion sécurisée."],
  ['1 après-midi', 'Un site prêt sans projet interminable.'],
  ['Tunisie', 'Support 24/7, facturation locale.'],
];

function ProofStrip() {
  return (
    <section
      className="border-t py-28 md:py-36"
      style={{borderColor: c.rule, background: c.ruleSoft}}
      aria-label="Points de confiance"
    >
      <Container>
        <Reveal>
          <div className="proof-grid grid gap-12 md:grid-cols-4">
            {proofItems.map(([value, label]) => (
              <div
                key={value}
                className="proof-card rounded border bg-white px-18 py-18"
                style={{borderColor: c.rule}}
              >
                <p
                  className="m-0 text-xl"
                  style={{fontWeight: 700, color: c.ink, lineHeight: 1.15}}
                >
                  {value}
                </p>
                <p
                  className="m-0 mt-6 text-sm"
                  style={{color: c.inkMuted, lineHeight: 1.45}}
                >
                  {label}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

const glassFeatures = [
  {
    img: '/images/landing/glass-cone-alpha.webp',
    alt: 'Cône de verre 3D aux reflets bleu, cyan et lime',
    title: 'En ligne en une après-midi',
    body: 'Créez votre compte, choisissez un modèle, publiez. Pas de projet interminable.',
  },
  {
    img: '/images/landing/glass-octahedron-alpha.webp',
    alt: 'Diamant de verre 3D aux reflets bleu, cyan et lime',
    title: '100% sans code',
    body: 'Textes, images et couleurs se modifient simplement, sans toucher au code.',
  },
  {
    img: '/images/landing/glass-star-alpha.webp',
    alt: 'Étoile de verre 3D aux reflets bleu, cyan et lime',
    title: 'Modèles soignés',
    body: 'Des bases propres, pensées pour mobile et desktop, prêtes à personnaliser.',
  },
  {
    img: '/images/landing/glass-slab-alpha.webp',
    alt: 'Gemme rectangulaire de verre 3D aux reflets bleu, cyan et lime',
    title: 'Gratuit pendant le lancement',
    body: "Hébergement, SSL et lien d'accès inclus. Aucun paiement demandé pour commencer.",
  },
];

function GlassFeatures() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="glass-features-title"
    >
      <Container>
        <Reveal className="mx-auto mb-48 max-w-720 text-center md:mb-64">
          <span className="eyebrow mb-20 inline-block">Pourquoi MRIGL</span>
          <h2
            id="glass-features-title"
            className="display text-3xl md:text-5xl"
            style={{lineHeight: 1.05, margin: '0 auto', maxWidth: '20ch'}}
          >
            Vraiment simple.{' '}
            <span className="gradient-text">Vraiment rapide.</span>
          </h2>
        </Reveal>

        <Reveal>
          <div className="glass-grid">
            {glassFeatures.map(feature => (
              <article key={feature.title} className="glass-card">
                <div className="glass-tile">
                  <img
                    src={feature.img}
                    alt={feature.alt}
                    loading="lazy"
                    width="620"
                    height="620"
                  />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </article>
            ))}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function GradientCta() {
  return (
    <section className="py-56 md:py-96" aria-labelledby="gradient-cta-title">
      <Container>
        <Reveal>
          <div className="gradient-band">
            <h2 id="gradient-cta-title" className="text-4xl md:text-6xl">
              Prêt à lancer votre projet ?
            </h2>
            <p>
              Profitez de la gratuité de lancement pour mettre votre site en
              ligne aujourd'hui, sans carte bancaire.
            </p>
            <Link to="/register" className="btn-white">
              Commencer maintenant
              <ArrowRight />
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function ExamplePreview() {
  // real screenshot of the MRIGL editor; falls back to the illustrated
  // mock if the asset is missing so the section never looks broken
  const [screenshotFailed, setScreenshotFailed] = useState(false);

  return (
    <section
      id="exemple"
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="example-title"
    >
      <Container className="grid items-center gap-48 md:grid-cols-[5fr_7fr] md:gap-64">
        <Reveal>
          <span className="eyebrow mb-20 inline-block">L'éditeur</span>
          <h2
            id="example-title"
            className="display mb-18 text-3xl md:text-5xl"
            style={{lineHeight: 1.08}}
          >
            Cliquez, modifiez, publiez. Sans code.
          </h2>
          <p
            className="mb-24 text-base leading-7"
            style={{color: c.inkMuted, maxWidth: 520}}
          >
            Voici le vrai éditeur MRIGL : cliquez sur un texte, une image ou un
            bouton, et modifiez-le directement. Sections prêtes à l'emploi,
            aperçu mobile, publication en un clic — pensé pour les freelances,
            artisans, cafés, cabinets et petits projets tunisiens.
          </p>
          <Link
            to="/register"
            className="btn-link inline-flex items-center gap-8 text-base font-semibold"
            style={{color: c.ink}}
          >
            Essayer l'éditeur gratuitement
            <ArrowRight />
          </Link>
        </Reveal>

        <Reveal>
          <div
            className="overflow-hidden rounded border bg-white p-12"
            style={{
              borderColor: c.rule,
              boxShadow:
                '0 28px 60px rgba(10, 10, 10, 0.08), 0 6px 18px rgba(10, 10, 10, 0.05)',
            }}
          >
            {screenshotFailed ? (
              <BrowserPreview compact />
            ) : (
              <EditorScreenshot
                loading="lazy"
                className="block h-auto w-full rounded"
                onError={() => setScreenshotFailed(true)}
              />
            )}
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

const steps = [
  {
    n: '01',
    title: 'Créez votre compte',
    body: 'Quelques minutes suffisent pour démarrer votre site.',
  },
  {
    n: '02',
    title: 'Choisissez un modèle',
    body: 'Portfolio, commerce local, restaurant, cabinet, projet ou marque personnelle.',
  },
  {
    n: '03',
    title: 'Modifiez et publiez',
    body: 'Changez textes, images et couleurs, puis mettez le site en ligne.',
  },
];

function HowItWorks() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="how-title"
    >
      <Container>
        <Reveal className="mx-auto mb-48 max-w-720 text-center md:mb-64">
          <span className="eyebrow mb-20 inline-block">Comment ça marche</span>
          <h2
            id="how-title"
            className="display text-3xl md:text-5xl"
            style={{lineHeight: 1.08, margin: '0 auto', maxWidth: '19ch'}}
          >
            En ligne <span className="gradient-text">sans technique</span>{' '}
            compliquée.
          </h2>
        </Reveal>

        <Reveal>
          <ol
            className="m-0 grid list-none gap-20 p-0 md:gap-24"
            style={{
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
            }}
          >
            {steps.map(step => (
              <li
                key={step.n}
                className="conversion-card rounded border bg-white p-24 md:p-30"
                style={{borderColor: c.rule}}
              >
                <span
                  className="mb-18 inline-flex h-34 w-48 items-center justify-center rounded text-sm font-bold tabular-nums"
                  style={{background: c.lime, color: c.ink}}
                  aria-hidden="true"
                >
                  {step.n}
                </span>
                <h3
                  className="mb-8 text-lg"
                  style={{fontWeight: 700, color: c.ink, lineHeight: 1.3}}
                >
                  {step.title}
                </h3>
                <p
                  className="m-0 text-sm"
                  style={{color: c.inkMuted, lineHeight: 1.6}}
                >
                  {step.body}
                </p>
              </li>
            ))}
          </ol>
        </Reveal>
      </Container>
    </section>
  );
}

const includedItems = [
  ['Hébergement', 'votre site est en ligne, sans configuration serveur.'],
  ["Lien d'accès inclus", 'une adresse claire à partager dès le départ.'],
  ['Certificat SSL', 'connexion sécurisée incluse par défaut.'],
  ['Domaine personnalisé', 'connectez votre propre domaine quand vous voulez.'],
  ['Modèles soignés', 'des bases propres pour mobile et desktop.'],
  ['Éditeur simple', 'textes, images et couleurs restent faciles à changer.'],
];

function Included() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule, background: c.ruleSoft}}
      aria-labelledby="included-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Inclus</span>
          <h2
            id="included-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.08}}
          >
            Tout ce qu'il faut, inclus pendant le lancement.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '36ch', lineHeight: 1.6}}>
            Aucun paiement demandé, aucun module obligatoire et aucun réglage
            technique avant de publier.
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none bg-white p-0">
            {includedItems.map(([lead, body]) => (
              <li
                key={lead}
                className="grid grid-cols-[1.6rem_1fr] items-start gap-16 px-20 py-22"
              >
                <span
                  className="mt-3 inline-flex h-20 w-20 items-center justify-center rounded-full"
                  style={{background: c.lime, color: c.ink}}
                  aria-hidden="true"
                >
                  <CheckMark />
                </span>
                <span style={{color: c.ink2, lineHeight: 1.55}}>
                  <b style={{fontWeight: 700, color: c.ink}}>{lead}</b> - {body}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
}

const launchBenefits = [
  'Création et publication de votre site',
  'Hébergement et certificat SSL inclus',
  "Lien d'accès prêt à partager",
  'Modèles et éditeur visuel inclus',
];

function LaunchOffer() {
  return (
    <section
      id="creer"
      className="border-t py-96 md:py-144"
      style={{borderColor: c.rule, background: c.bg}}
      aria-labelledby="launch-offer-title"
    >
      <Container>
        <Reveal className="mx-auto mb-52 max-w-680 text-center">
          <span className="eyebrow mb-20 inline-block">Offre de lancement</span>
          <h2
            id="launch-offer-title"
            className="display mb-18 text-3xl md:text-5xl"
            style={{color: c.ink, fontWeight: 800, lineHeight: 1.08}}
          >
            <span className="gradient-text">Votre site est gratuit</span>{' '}
            pendant le lancement.
          </h2>
          <p
            className="max-w-560 mx-auto text-lg leading-7"
            style={{color: c.inkMuted}}
          >
            Créez, personnalisez et publiez maintenant. Aucun lien de paiement,
            aucune carte bancaire et aucun débit ne sont nécessaires.
          </p>
        </Reveal>

        <Reveal>
          <div className="launch-offer-card max-w-900 mx-auto">
            <div className="launch-offer-price">
              <span>Gratuité de lancement</span>
              <strong>0 TND</strong>
              <p>Aujourd'hui, pour commencer.</p>
            </div>
            <div className="launch-offer-content">
              <h3>Tout le nécessaire pour publier</h3>
              <ul>
                {launchBenefits.map(item => (
                  <li key={item}>
                    <span aria-hidden="true">
                      <CheckMark />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mrigl-gradient-button">
                Créer mon site gratuitement
                <ArrowRight />
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal className="mx-auto mt-28 max-w-680 text-center">
          <p className="text-sm" style={{color: c.inkMuted, lineHeight: 1.6}}>
            Cette offre est temporaire. Nous vous informerons clairement avant
            toute évolution tarifaire. Aucun paiement ne sera effectué sans
            votre accord explicite.
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

const audienceFor = [
  [
    'Freelances et indépendants',
    'portfolio, services, contact et crédibilité.',
  ],
  [
    'Créateurs et marques personnelles',
    'un lien central hors des réseaux sociaux.',
  ],
  ['Artisans et petites entreprises', 'une vitrine simple à partager.'],
  ['Restaurants, cafés et salons', 'horaires, adresse, menu, contact.'],
  ['Cabinets et consultants', 'présentation claire et demande de contact.'],
  ['Projets en démarrage', 'une présence en ligne avant de tout construire.'],
];

const audienceNotFor = [
  'Boutique en ligne complète avec panier et paiement.',
  'Espace membre, tableau de bord ou comptes utilisateurs.',
  'Application web sur mesure ou intégrations complexes.',
  'Site multilingue avancé avec contenu lourd.',
];

function Audience() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="audience-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Pour qui</span>
          <h2
            id="audience-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '18ch', lineHeight: 1.08}}
          >
            Fait pour les sites simples qui doivent inspirer confiance.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '38ch', lineHeight: 1.6}}>
            MRIGL va droit au but. Il aide les petits projets à être trouvés,
            compris et contactés.
          </p>
        </Reveal>

        <Reveal>
          <ul className="who-list m-0 list-none p-0">
            {audienceFor.map(([lead, body], i) => (
              <li
                key={lead}
                className="grid grid-cols-[2.4rem_1fr] items-baseline gap-x-10 py-22"
                style={{lineHeight: 1.55}}
              >
                <span
                  className="text-sm tabular-nums"
                  style={{color: c.inkSoft, fontWeight: 700}}
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>
                  <b style={{fontWeight: 700, color: c.ink}}>{lead}</b>{' '}
                  <span style={{color: c.ink2}}>- {body}</span>
                </span>
              </li>
            ))}
          </ul>

          <aside
            className="mt-36 rounded border px-24 py-24"
            style={{
              borderColor: c.rule,
              background: c.ruleSoft,
              color: c.inkMuted,
              lineHeight: 1.55,
              fontSize: '0.95rem',
            }}
          >
            <p
              className="mb-12 text-sm"
              style={{color: c.ink, fontWeight: 700, margin: 0}}
            >
              Ce n'est pas le bon choix si vous avez besoin de :
            </p>
            <ul className="m-0 list-none space-y-8 p-0">
              {audienceNotFor.map(item => (
                <li key={item} className="flex items-start gap-10">
                  <span
                    className="mt-8 inline-block h-6 w-6 shrink-0 rounded-full"
                    style={{background: c.inkSoft}}
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </aside>
        </Reveal>
      </Container>
    </section>
  );
}

const trustSignals = [
  'Aucun paiement demandé pendant le lancement.',
  'Support 24/7, répondu par un humain.',
  'Hébergement et certificat SSL inclus.',
  'Aucun engagement long. Vous gardez le contrôle.',
];

function Trust() {
  return (
    <section
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule, background: c.bg}}
      aria-labelledby="trust-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Confiance</span>
          <h2
            id="trust-title"
            className="display mb-20 text-3xl md:text-5xl"
            style={{maxWidth: '15ch', lineHeight: 1.08}}
          >
            Construit en Tunisie, pour la Tunisie.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '38ch', lineHeight: 1.6}}>
            MRIGL parle aux petites structures locales : lancement gratuit,
            support simple et une expérience pensée pour publier sans se perdre.
          </p>
        </Reveal>

        <Reveal>
          <ul className="included-list m-0 list-none p-0">
            {trustSignals.map(item => (
              <li
                key={item}
                className="grid grid-cols-[1.4rem_1fr] items-start gap-16 py-22"
              >
                <span
                  className="lime-mark mt-10 inline-block h-8 w-8"
                  aria-hidden="true"
                />
                <span style={{color: c.ink2, lineHeight: 1.55}}>{item}</span>
              </li>
            ))}
          </ul>

          <p
            className="mt-24 max-w-500 text-sm"
            style={{color: c.inkMuted, lineHeight: 1.6}}
          >
            Une question avant de commencer ?{' '}
            <Link
              to="/contact"
              className="btn-link inline-flex items-center gap-6 font-semibold"
              style={{color: c.ink}}
            >
              Écrivez-nous
              <ArrowRight />
            </Link>
          </p>
        </Reveal>
      </Container>
    </section>
  );
}

const faqs = [
  [
    'Combien coûte MRIGL pendant le lancement ?',
    'MRIGL est gratuit pendant la phase de lancement. Aucun lien de paiement ni carte bancaire ne sont nécessaires pour commencer.',
  ],
  [
    'Que se passe-t-il après la période de lancement ?',
    'Nous vous informerons clairement avant toute évolution de l’offre. Aucun paiement ne sera effectué sans votre accord explicite.',
  ],
  [
    'Combien de temps pour publier mon site ?',
    'Vous pouvez préparer une première version en une après-midi si vos textes et images sont prêts.',
  ],
  [
    'Puis-je connecter mon propre domaine ?',
    "Oui. Vous pouvez utiliser votre domaine ou garder le lien d'accès fourni par MRIGL.",
  ],
  [
    'Dois-je savoir coder ?',
    'Non. MRIGL est pensé pour modifier une page simplement, sans toucher au code.',
  ],
];

function Faq() {
  return (
    <section
      id="faq"
      className="border-t py-80 md:py-128"
      style={{borderColor: c.rule}}
      aria-labelledby="faq-title"
    >
      <Container className="grid gap-48 md:grid-cols-[5fr_7fr] md:gap-80">
        <Reveal className="md:sticky md:top-96 md:self-start">
          <span className="eyebrow mb-20 inline-block">Questions</span>
          <h2
            id="faq-title"
            className="display mb-16 text-3xl md:text-5xl"
            style={{maxWidth: '14ch', lineHeight: 1.08}}
          >
            Les réponses avant de créer votre compte.
          </h2>
          <p style={{color: c.inkMuted, maxWidth: '36ch', lineHeight: 1.6}}>
            L'objectif est simple : vous savez exactement ce qui est inclus
            avant de commencer.
          </p>
        </Reveal>

        <Reveal>
          <ul className="who-list m-0 list-none p-0">
            {faqs.map(([question, answer]) => (
              <li key={question} className="py-26">
                <p
                  className="mb-8 text-lg"
                  style={{fontWeight: 700, color: c.ink, margin: 0}}
                >
                  {question}
                </p>
                <p
                  className="text-base"
                  style={{
                    color: c.ink2,
                    margin: '8px 0 0 0',
                    lineHeight: 1.6,
                  }}
                >
                  {answer}
                </p>
              </li>
            ))}
          </ul>
        </Reveal>
      </Container>
    </section>
  );
}

function FinalCta() {
  return (
    <section
      className="border-t py-56 md:py-96"
      style={{borderColor: c.rule}}
      aria-labelledby="final-cta-title"
    >
      <Container>
        <Reveal>
          <div className="gradient-band">
            <h2 id="final-cta-title" className="text-4xl md:text-6xl">
              Lancez votre site !
            </h2>
            <p>
              Profitez de la gratuité de lancement. Créez une page claire que
              vous pouvez modifier quand vous voulez.
            </p>
            <Link to="/register" className="btn-white">
              Créer mon site gratuitement
              <ArrowRight />
            </Link>
            <p
              className="mt-24 text-xs"
              style={{marginBottom: 0, color: 'rgba(255, 255, 255, 0.8)'}}
            >
              Aucun paiement demandé. Support 24/7. SSL inclus.
            </p>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M10 1.7l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.32l-4.94 2.6.94-5.51-4-3.9 5.53-.8L10 1.7z" />
    </svg>
  );
}

interface EditorScreenshotProps {
  loading: 'eager' | 'lazy';
  className: string;
  onError?: () => void;
}

function EditorScreenshot({
  loading,
  className,
  onError,
}: EditorScreenshotProps) {
  return (
    <img
      src="/images/landing/editor-screenshot.webp"
      alt="Éditeur visuel MRIGL avec panneau de styles en français et aperçu du site modifié en direct"
      width={2904}
      height={1518}
      loading={loading}
      decoding="async"
      className={className}
      onError={onError}
    />
  );
}

function BrowserPreview({compact = false}: {compact?: boolean}) {
  return (
    <div className={`browser-frame ${compact ? 'browser-frame-compact' : ''}`}>
      <div className="browser-window">
        <div className="browser-top">
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <div className="url-bar">mrigl.site/rania</div>
        </div>
        <div className="site-preview">
          <div className="preview-left">
            <div className="preview-badge">Disponible à Tunis</div>
            <h2>Design clair pour marques locales.</h2>
            <p>
              Une page simple, crédible et prête à partager pour présenter vos
              services, portfolio, horaires, adresse et contact.
            </p>
            <div className="preview-buttons">
              <span className="preview-mini-btn">Me contacter</span>
              <span className="preview-mini-btn alt">Portfolio</span>
            </div>
          </div>
          <div className="preview-right">
            <div className="preview-card">
              <div className="preview-card-top">
                <span>Rania Studio</span>
                <span>MRIGL</span>
              </div>
              <div className="preview-image" />
              <div>
                <strong>Exemple de page publiée</strong>
                <p>
                  Services, portfolio, contact et informations essentielles.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
